'use client';

import type { UseQueryResult } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import useProductsInventory, {
  DEFAULT_PRODUCTS_INVENTORY,
  type UseProductsInventory,
} from '@/app/[locale]/product/_hooks/useProductsInventory';
import { useCartContext } from '@/app/contexts/CartProvider';
import logToDatadog from '@/logger/logToDatadog';
import type { ListProduct } from '@/queries/Products';
import { useProductsData } from '@/queries/Products';
import type { RefreshCart_cart_refreshCart_cart_lineItems } from '@/queries/types/RefreshCart';

interface PDPValues extends UseProductsInventory {
  chargerCount: number;
  data?: ListProduct[];
  error: unknown;
  isLoadingPdpData: boolean;
  isSuccess: boolean;
  itemCountForIcon: number;
  lineItems: (RefreshCart_cart_refreshCart_cart_lineItems | null)[];
  maxChargerQuantity: number | null;
  maxRingQuantity: number | null;
  ringCount: number;
}

export const INITIAL_PDP_VALUES: PDPValues = {
  chargerCount: 0,
  data: [],
  error: null,
  isLoadingPdpData: true,
  isSuccess: false,
  itemCountForIcon: 0,
  lineItems: [],
  maxChargerQuantity: 10,
  maxRingQuantity: 3,
  ringCount: 0,
  ...DEFAULT_PRODUCTS_INVENTORY,
};

type PDPProvider = PDPValues & UseQueryResult<ListProduct[], Error>;
export type PDPContextType =
  | (PDPProvider & UseProductsInventory)
  | (PDPValues & UseProductsInventory);

const PDPContext = createContext<PDPContextType>(INITIAL_PDP_VALUES);

export const usePDPContext = (): PDPContextType => {
  return useContext(PDPContext);
};

const Provider = ({ children }: PropsWithChildren) => {
  const inventory = useProductsInventory();
  // get cart
  const { refreshCart, refreshCartError, refreshCartLoading } =
    useCartContext();
  const cart = refreshCart?.cart;

  if (refreshCartError) {
    logToDatadog({
      error: refreshCartError,
      message: 'Error fetching cart in PDPProvider',
      module: 'cart',
    });
  }

  // get products
  const products = useProductsData({
    includeInventory: true,
  });

  const isLoadingPdpData = refreshCartLoading || products.isLoading;

  const values: PDPProvider = useMemo(() => {
    const { chargerCount, itemCountForIcon, lineItems, ringCount } =
      cart ?? INITIAL_PDP_VALUES;

    const { maxChargerQuantity, maxRingQuantity } = cart ?? INITIAL_PDP_VALUES;

    return {
      chargerCount: chargerCount!,
      itemCountForIcon: itemCountForIcon!,
      lineItems: lineItems!,
      maxChargerQuantity: maxChargerQuantity,
      maxRingQuantity: maxRingQuantity,
      ringCount: ringCount!,
      isLoadingPdpData,
      ...products,
      ...inventory,
    };
  }, [cart, inventory, isLoadingPdpData, products]);

  return <PDPContext.Provider value={values}>{children}</PDPContext.Provider>;
};

export default Provider;
